import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMatches } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import type { Match } from "@/lib/types";
import { PageHeader } from "./components/PageHeader";
import { CoachMatchUploadModal } from "./components/CoachMatchUploadModal";

/**
 * Coach > Matches — list the active team's matches with create/upload
 * affordances (M12b).
 *
 * Reads `GET /api/matches?team_id=…` scoped by team ONLY — `season_id`
 * is deliberately suppressed (`seasonId: null`) so the list shows every
 * match for the team across all seasons. Seasons are globally shared;
 * the season name is just an inline detail on each row, not a filter.
 * Injecting `season_id` would narrow `/api/matches` to one season and
 * hide the team's other-season matches (which still show on the
 * unscoped public site).
 * Coaches get the `match:write` capability for teams they're a member of
 * (granted in tenancy.py M12b); the tenancy guard on POST + upload-session
 * endpoints rejects cross-team writes, so coaches can ONLY create + upload
 * matches for the team currently in scope.
 *
 * Two CTAs on every row:
 *   - "Edit" → reopens the same `MatchEditModal` admin uses.
 *   - "Upload video" → opens an upload session modal that drives the
 *     existing chunked-upload API for the chosen slot.
 *
 * The page-level "Add match" button lives in the PageHeader actions
 * slot. New-match flow: open `MatchEditModal` with `match=null`, then
 * on save offer to go straight into upload (the saved match is passed
 * via the `onSaved` callback).
 */
export function CoachMatchesPage() {
  const teamId = useScopeStore((s) => s.teamId);
  const teamName = useScopeStore((s) => s.active?.team.name ?? "");
  const matchesQuery = useQuery({
    queryKey: useScopedKey("coach-matches"),
    // `getMatches()` scopes by team only (suppresses `season_id`) so the
    // list shows the team's matches across every season — see its
    // docstring + the route's tktic#8 behavior.
    queryFn: getMatches,
    enabled: !!teamId,
  });

  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [uploadTarget, setUploadTarget] = useState<Match | null>(null);
  // Track the most-recently-created match so we can offer to advance
  // straight into the upload flow once the modal closes (M11+ legacy
  // pattern — the coach's expected next step after "create match" is
  // "upload video").
  const pendingUpload = useRef<Match | null>(null);

  const matches = matchesQuery.data ?? [];
  const sorted = useMemo(() => {
    return [...matches].sort((a, b) => {
      const da = a.date || "";
      const db = b.date || "";
      if (da !== db) return db.localeCompare(da);
      return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
    });
  }, [matches]);

  if (!teamId) {
    return (
      <div className="flex flex-col gap-5" data-testid="coach-matches-page">
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="coach-matches-no-team"
        >
          No team selected.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="coach-matches-page">
      <PageHeader
        title="Matches"
        description={
          <>
            {teamName ? `${teamName} · ` : ""}Create matches and upload video
            for your team.
          </>
        }
        actions={
          <Button
            type="button"
            onClick={() => setEditTarget("new")}
            data-testid="coach-matches-add"
            size="sm"
          >
            Add Match
          </Button>
        }
      />

      {matchesQuery.isLoading ? (
        <p
          data-testid="coach-matches-loading"
          className="font-mono text-sm text-ink-muted"
        >
          Loading matches…
        </p>
      ) : matchesQuery.isError ? (
        <p
          data-testid="coach-matches-error"
          className="font-mono text-sm text-destructive"
        >
          Couldn&rsquo;t load matches.
        </p>
      ) : sorted.length === 0 ? (
        <p
          data-testid="coach-matches-empty"
          className="font-mono text-sm text-ink-muted"
        >
          No matches yet. Use{" "}
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            className="text-accent underline underline-offset-2 hover:no-underline"
          >
            Add Match
          </button>{" "}
          to create the first one.
        </p>
      ) : (
        <ul
          data-testid="coach-matches-list"
          role="list"
          className="flex flex-col gap-2"
        >
          {sorted.map((match) => (
            <li key={match.id}>
              <CoachMatchRow
                match={match}
                onEdit={() => setEditTarget(match)}
                onUpload={() => setUploadTarget(match)}
              />
            </li>
          ))}
        </ul>
      )}

      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          onSaved={(saved) => {
            // On a fresh create we remember the new match so the
            // close handler below can prompt for upload. On edit we
            // don't carry pending state — the user explicitly opens
            // upload from the row.
            if (editTarget === "new") pendingUpload.current = saved;
          }}
          onClose={() => {
            setEditTarget(null);
            const next = pendingUpload.current;
            pendingUpload.current = null;
            if (next) setUploadTarget(next);
          }}
        />
      ) : null}

      {uploadTarget ? (
        <CoachMatchUploadModal
          match={uploadTarget}
          onClose={() => setUploadTarget(null)}
        />
      ) : null}
    </div>
  );
}

function CoachMatchRow({
  match,
  onEdit,
  onUpload,
}: {
  match: Match;
  onEdit: () => void;
  onUpload: () => void;
}) {
  const slots = match.video_status ?? {};
  const ready = Object.values(slots).filter((s) => s === "ready").length;
  const transcoding = Object.values(slots).filter(
    (s) => s === "transcoding",
  ).length;
  const errored = Object.values(slots).filter((s) => s === "error").length;
  // Resolve season name from the cached /api/me payload's global
  // `seasons` array — no extra fetch. Falls through to nothing when the
  // user has no /api/me yet (anon view won't render this surface anyway).
  const seasonName = useAuthStore((s) => {
    if (!s.me || !match.season_id) return null;
    return s.me.seasons.find((sn) => sn.id === match.season_id)?.name ?? null;
  });
  return (
    <article
      data-testid="coach-match-row"
      data-match-id={match.id}
      className="flex flex-col gap-2 rounded border border-rule bg-surface p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-1 min-w-0">
        <p className="font-display text-sm text-ink truncate">
          {match.home_team || "—"} vs {match.away_team || "—"}
        </p>
        <p className="font-mono text-[0.65rem] uppercase tracking-mono text-ink-muted">
          {match.date || "no date"}
          {match.time ? ` · ${match.time}` : ""}
          {match.location ? ` · ${match.location}` : ""}
          {seasonName ? (
            <>
              {" · "}
              <span data-testid="coach-match-row-season">{seasonName}</span>
            </>
          ) : null}
        </p>
        <p className="flex flex-wrap gap-1.5 font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted">
          <Badge variant={ready > 0 ? "good" : "muted"} data-testid="coach-match-row-ready">
            {ready} ready
          </Badge>
          {transcoding > 0 ? (
            <Badge variant="flag" data-testid="coach-match-row-transcoding">
              {transcoding} transcoding
            </Badge>
          ) : null}
          {errored > 0 ? (
            <Badge variant="outline" data-testid="coach-match-row-error" className="border-destructive text-destructive">
              {errored} error
            </Badge>
          ) : null}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={onEdit}
          data-testid="coach-match-row-edit"
          variant="outline"
          size="sm"
        >
          Edit
        </Button>
        <Button
          type="button"
          onClick={onUpload}
          data-testid="coach-match-row-upload"
          size="sm"
        >
          Upload video
        </Button>
      </div>
    </article>
  );
}
